import {GA_MEASUREMENT_ID} from '@/config';
import {readConsent} from '@/lib/consent';

/**
 * GA4 events tracked in addition to GA4's automatic "enhanced measurement"
 * (page views incl. client-side navigation, scrolls, outbound clicks, file downloads).
 */
export type AnalyticsEvents = {
  // --- Leads & contact --------------------------------------------------------------------------
  /** Contact form sent successfully (key conversion). */
  generate_lead: {form_name: string};
  /** First interaction with a form field. */
  form_start: {form_name: string};
  /** Submit button pressed. */
  form_submit: {form_name: string};
  /** Submission rejected by validation (field names only, never values). */
  form_validation_error: {form_name: string; fields: string};
  /** Form could not be sent (server/SMTP). */
  form_error: {form_name: string; reason: string};
  /** Phone, e-mail and map links (also auto-detected for `tel:`/`mailto:` links anywhere). */
  contact_click: {method: 'phone' | 'email' | 'maps'; location: string};
  /** Call-to-action buttons (e.g. "Kontakt aufnehmen"). */
  cta_click: {cta: string; location: string};

  // --- Navigation & engagement ------------------------------------------------------------------
  /** Link clicks inside a `data-track-area` (header, footer, breadcrumbs, content, …). */
  navigation_click: {location: string; link_text: string; link_url: string};
  /** Mobile menu opened. */
  menu_open: {location: string};
  /** Scroll depth milestones per page (25/50/75/90/100 %). */
  scroll_depth: {percent: number};
  /** Text copied (length only, never the text). */
  copy_text: {text_length: number};

  // --- Content ----------------------------------------------------------------------------------
  /** Card clicks (categories, products, news, references). */
  select_content: {content_type: string; item_id: string; location: string};
  /** Product detail page viewed (GA4 e-commerce-style item). */
  view_item: {item_id: string; item_name: string; item_category: string};
  /** Product list of a category viewed. */
  view_item_list: {item_list_id: string; item_list_name: string; items_count: number};
  /** Product datasheet / document download. */
  datasheet_download: {file_name: string; product: string};
  /** Product tab switched. */
  view_product_tab: {product: string; tab: string};
  /** Image lightbox opened. */
  gallery_open: {context: string; image_index: number};
  /** Image shown in the lightbox (incl. swiping). */
  gallery_image_view: {context: string; image_index: number};
  /** Embedded video started / watched to the end. */
  video_start: {video_url: string};
  video_complete: {video_url: string};

  // --- Technical --------------------------------------------------------------------------------
  /** Analytics consent granted in the cookie banner. */
  consent_update: {analytics_storage: 'granted'};
  /** 404 page shown (finds broken legacy links). */
  page_not_found: {page_path: string};
  /** Unexpected error boundary shown. */
  app_error: {digest: string};
  /** Core Web Vitals. */
  web_vitals: {metric_name: string; metric_id: string; value: number};
};

export type AnalyticsEventName = keyof AnalyticsEvents;

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: Gtag;
    dataLayer?: unknown[];
  }
}

/**
 * Sends a GA4 event — only when analytics is enabled for this environment and the visitor consented.
 * Events fired before gtag.js has loaded are queued in the dataLayer.
 */
export const track = <N extends AnalyticsEventName>(name: N, params: AnalyticsEvents[N]) => {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined' || readConsent() !== 'accepted') {
    return;
  }
  window.dataLayer = window.dataLayer ?? [];
  window.gtag =
    window.gtag ??
    function gtag() {
      // gtag.js expects the `arguments` object itself
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  window.gtag('event', name, params);
};

const ATTR_PREFIX = 'data-track';

/**
 * Marks a container (header, footer, breadcrumbs, content …): every link inside it without its own
 * `data-track` is reported as `navigation_click` with this area as location.
 */
export const trackArea = (location: string) => ({'data-track-area': location});

/**
 * Declarative tracking for Server Components: spread onto a clickable element.
 * `<a {...trackAttrs('contact_click', {method: 'phone', location: 'footer'})}>`
 * The click listener in <Analytics /> picks these up.
 */
export const trackAttrs = <N extends AnalyticsEventName>(name: N, params: AnalyticsEvents[N]) =>
  Object.fromEntries([
    [ATTR_PREFIX, name],
    ...Object.entries(params).map(([key, value]) => [`${ATTR_PREFIX}-${key}`, String(value)]),
  ]) as Record<string, string>;

/**
 * Handles a click anywhere on the page:
 * 1. explicit `data-track` attributes (see trackAttrs),
 * 2. `tel:` / `mailto:` links → contact_click,
 * 3. links inside a `data-track-area` → navigation_click.
 */
export const trackClick = (target: HTMLElement) => {
  const explicit = target.closest<HTMLElement>('[data-track]');
  if (explicit) {
    trackFromElement(explicit);
    return;
  }
  const link = target.closest<HTMLAnchorElement>('a[href]');
  if (!link) {
    return;
  }
  const location = link.closest<HTMLElement>('[data-track-area]')?.dataset.trackArea ?? 'page';
  const href = link.getAttribute('href') ?? '';
  if (href.startsWith('tel:') || href.startsWith('mailto:')) {
    track('contact_click', {method: href.startsWith('tel:') ? 'phone' : 'email', location});
    return;
  }
  if (location !== 'page') {
    track('navigation_click', {
      location,
      link_text: (link.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 100),
      link_url: link.href,
    });
  }
};

/** Reads `data-track*` attributes from an element (see trackAttrs). */
const trackFromElement = (element: HTMLElement) => {
  const name = element.dataset.track as AnalyticsEventName | undefined;
  if (!name) {
    return;
  }
  const params: Record<string, string | number> = {};
  for (const attr of Array.from(element.attributes)) {
    if (attr.name.startsWith(`${ATTR_PREFIX}-`)) {
      const key = attr.name.slice(ATTR_PREFIX.length + 1);
      params[key] = /^\d+$/.test(attr.value) ? Number(attr.value) : attr.value;
    }
  }
  track(name, params as never);
};
